'use strict';

// Vue
import Vue from 'vue';

// Axios
import Axios from 'axios';
import store from '@/store';
import router from '@/router';

const api = (instanceConfig) => {
    const { baseURL, headers } = instanceConfig || {};

    const vue = new Vue({ name: 'api' });

    let CancelToken = Axios.CancelToken,
        cancelApiCall = {},
        generateID = () =>
            Math.floor(Math.random() * Date.now())
                .toString(36)
                .substr(2);

    // Process ENV Details
    // console.log(process.env, 'env');

    // Create New Axios Instance And Default Configurations
    const axiosInstance = Axios.create({ baseURL, headers });

    // console.log(axiosInstance.defaults, 'axios instance');
    axiosInstance.defaults.withCredentials = true;

    axiosInstance.interceptors.request.use(
        (config) => {
            // console.log(config);
            store.commit('form/SET_ERRORS_EMPTY');
            vue.$eventBus.$emit('progress', 'initiate'); // For every request start the progress
            return config;
        },
        (error) => {
            // console.log(error);
            return Promise.reject(error);
        }
    );

    axiosInstance.interceptors.response.use(
        (response) => {
            // console.log(response);
            vue.$eventBus.$emit('progress', 'success'); // Finish when a response is received
            return response;
        },
        (error) => {
            // Validation Errors
            // console.log('%c' + error, 'background: red; color: white');
            if (error.response && error.response.status == 422 && error.response.data.errors) {
                store.commit('form/SET_ERRORS', error.response.data.errors);
            } else if (error.response && error.response.status == 401) {
                vue.$eventBus.$emit('clear-data');
            } else if (error.response && error.response.status) {
                Vue.notify({
                    group: 'main-notifications',
                    type: 'error',
                    duration: 5000,
                    title: router.history.current.meta.moduleTitle || 'Content Management',
                    text: error.response ? error.response.data?.message || error.response.statusText : error
                });
            }

            if (/cancel/gi.test(error.message)) {
                vue.$eventBus.$emit('progress', 'stop'); // Stop when a response is canceled
            } else {
                vue.$eventBus.$emit('progress', 'error'); // Fail when a response is failed
            }
            return Promise.reject(error);
        }
    );

    return {
        config: { baseURL, headers },
        getAuthorization() {
            return axiosInstance.defaults.headers.common['Authorization'];
        },
        setAuthorization(token) {
            // console.log(token);

            // Set Authorization
            if (token) {
                axiosInstance.defaults.headers.common['Authorization'] = `Bearer ${token}`;
            } else {
                delete axiosInstance.defaults.headers.common['Authorization'];
            }
        },
        cancelCurrentApiCall() {
            const cancelSourceList = Object.keys(cancelApiCall);
            // console.log(cancelSourceList, 'cancel source list');
            if (cancelSourceList.length > 0) {
                cancelSourceList.forEach((cancelSource) => {
                    cancelApiCall[cancelSource]('Cancel Current API Call.');
                });
                cancelApiCall = {};
            }
        },
        get(payload) {
            const { url, config, hasCancel } = payload || {},
                cancelSourceID = `cancel_source_${generateID()}`;
            let cancelToken = '';
            if (hasCancel !== false) {
                cancelToken = new CancelToken((cancelSource) => {
                    cancelApiCall = { ...cancelApiCall, [cancelSourceID]: cancelSource };
                });
            } else {
                // console.log(hasCancel, 'doesnt have cancel');
            }

            // console.log(generateID(), 'gen id');
            return new Promise((resolve, reject) => {
                axiosInstance
                    .get(url, {
                        ...config,
                        cancelToken: cancelToken
                    })
                    .then((response) => {
                        // console.log(response);
                        delete cancelApiCall[cancelSourceID];
                        resolve(response);
                    })
                    .catch((error) => {
                        // console.log(error);
                        delete cancelApiCall[cancelSourceID];
                        reject(error);
                    });
            });
        },
        post(payload) {
            const { url, data, config, hasCancel } = payload || {},
                cancelSourceID = `cancel_source_${generateID()}`;

            let cancelToken = '';
            if (hasCancel !== false) {
                cancelToken = new CancelToken((cancelSource) => {
                    cancelApiCall = { ...cancelApiCall, [cancelSourceID]: cancelSource };
                });
            } else {
                // console.log(hasCancel, 'doesnt have cancel');
            }
            return new Promise((resolve, reject) => {
                axiosInstance
                    .post(url, data, {
                        ...config,
                        cancelToken: cancelToken
                    })
                    .then((response) => {
                        // console.log(response);
                        delete cancelApiCall[cancelSourceID];
                        resolve(response);
                    })
                    .catch((error) => {
                        // console.log(error);
                        delete cancelApiCall[cancelSourceID];
                        reject(error);
                    });
            });
        },
        put(payload) {
            const { url, data, config } = payload || {},
                cancelSourceID = `cancel_source_${generateID()}`;
            return new Promise((resolve, reject) => {
                axiosInstance
                    .put(url, data, {
                        ...config,
                        cancelToken: new CancelToken((cancelSource) => {
                            cancelApiCall = { ...cancelApiCall, [cancelSourceID]: cancelSource };
                        })
                    })
                    .then((response) => {
                        // console.log(response);
                        delete cancelApiCall[cancelSourceID];
                        resolve(response);
                    })
                    .catch((error) => {
                        // console.log(error);
                        delete cancelApiCall[cancelSourceID];
                        reject(error);
                    });
            });
        },
        delete(payload) {
            const { url, config } = payload || {},
                cancelSourceID = `cancel_source_${generateID()}`;
            return new Promise((resolve, reject) => {
                axiosInstance
                    .delete(url, {
                        ...config,
                        cancelToken: new CancelToken((cancelSource) => {
                            cancelApiCall = { ...cancelApiCall, [cancelSourceID]: cancelSource };
                        })
                    })
                    .then((response) => {
                        // console.log(response);
                        delete cancelApiCall[cancelSourceID];
                        resolve(response);
                    })
                    .catch((error) => {
                        // console.log(error);
                        delete cancelApiCall[cancelSourceID];
                        reject(error);
                    });
            });
        }
    };
};

export default api;
